import React, { Component, createRef } from "react";
import html2canvas from "html2canvas";
import CloseIcon from "@material-ui/icons/Close";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import CircularProgress from "@material-ui/core/CircularProgress";
import LazyImage from "../LazyImage/LazyImage";
import constants from "../Constants/constants";
import { categorizePreviewCards, getUniqueCards } from "./deckUtils";

export default class PlaymatView extends Component {
  constructor(props) {
    super(props);
    this.state = { capturing: false };
    this.containerRef = createRef();
  }

  handleScreenshot = async () => {
    const { deck, onShowToast } = this.props;
    if (!this.containerRef.current) return;

    this.setState({ capturing: true });

    try {
      // Force desktop layout for screenshot on mobile
      const container = this.containerRef.current;
      container.style.minWidth = "1200px";
      await new Promise((r) => setTimeout(r, 200));

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scrollY: -window.scrollY,
        windowWidth: container.scrollWidth,
        windowHeight: container.scrollHeight,
      });

      container.style.minWidth = "";

      const deckName = (deck?.name || "deck").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fileName = `${deckName}-playmat.png`;

      // Try Web Share API on mobile only (desktop browsers open an OS share sheet instead of saving)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile && navigator.share && navigator.canShare) {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
        const file = new File([blob], fileName, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `${deck?.name || "Deck"} - Table View` });
          if (onShowToast) onShowToast("Screenshot shared!");
          return;
        }
      }

      // Fallback: download link (works on desktop and Android)
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (onShowToast) onShowToast("Screenshot saved!");
    } catch (err) {
      console.error("Screenshot failed:", err);
      if (this.containerRef.current) this.containerRef.current.style.minWidth = "";
      if (onShowToast) onShowToast("Screenshot failed");
    } finally {
      this.setState({ capturing: false });
    }
  };

  renderCard(card, i, onOpenModal) {
    return (
      <div
        key={`${card._id}-${i}`}
        className="playmat-card"
        onClick={() => onOpenModal(card, true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenModal(card, true);
          }
        }}
        aria-label={`View ${card.name}`}
      >
        <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
      </div>
    );
  }

  renderCardFlat(entries, onOpenModal) {
    return (
      <div className="playmat-turn-cards">
        {entries.flatMap(({ card, count }) =>
          Array.from({ length: count }, (_, i) => this.renderCard(card, i, onOpenModal))
        )}
      </div>
    );
  }

  renderAbilities(entries, onOpenModal) {
    return (
      <div className="playmat-abilities-row">
        {entries.flatMap(({ card, count }) =>
          Array.from({ length: count }, (_, i) => this.renderCard(card, i, onOpenModal))
        )}
      </div>
    );
  }

  renderMainSections(characters, strategies, abilities, onOpenModal, deck) {
    return (
      <>
        {characters.length > 0 && (
          <div className="playmat-section">
            <h3 className="playmat-section-title">
              Characters
              <span className="playmat-section-count">
                {characters.reduce((s, e) => s + e.count, 0)}
              </span>
            </h3>
            <div className="playmat-groups">
              {this.renderCardFlat(characters, onOpenModal)}
            </div>
          </div>
        )}

        {strategies.length > 0 && (
          <div className="playmat-section">
            <h3 className="playmat-section-title">
              Strategies
              <span className="playmat-section-count">
                {strategies.reduce((s, e) => s + e.count, 0)}
              </span>
            </h3>
            <div className="playmat-groups">
              {this.renderCardFlat(strategies, onOpenModal)}
            </div>
          </div>
        )}

        {abilities.length > 0 && (
          <div className="playmat-section">
            <h3 className="playmat-section-title">
              Abilities
              <span className="playmat-section-count">
                {abilities.reduce((s, e) => s + e.count, 0)}
              </span>
            </h3>
            {this.renderAbilities(abilities, onOpenModal)}
          </div>
        )}

        {(() => {
          const sideDeck = deck.sideDeck || [];
          if (sideDeck.length === 0) return null;
          const sideUnique = getUniqueCards(sideDeck);
          return (
            <div className="playmat-section playmat-side-deck">
              <h3 className="playmat-section-title">
                Side Deck
                <span className="playmat-section-count">{sideDeck.length}</span>
              </h3>
              <div className="playmat-turn-cards">
                {sideUnique.flatMap(({ card, count }) =>
                  Array.from({ length: count }, (_, i) => this.renderCard(card, i, onOpenModal))
                )}
              </div>
            </div>
          );
        })()}
      </>
    );
  }

  render() {
    const { deck, onOpenModal, onClose } = this.props;

    if (!deck) return null;

    const cards = deck.cards || [];
    const { characters, strategies, abilities, field } = categorizePreviewCards(cards);

    return (
      <div className="playmat-overlay" onClick={onClose} role="dialog" aria-modal="true">
        {this.state.capturing && (
          <div className="playmat-capturing-overlay">
            <CircularProgress size={32} style={{ color: "rgb(255, 186, 6)" }} />
            <span>Capturing...</span>
          </div>
        )}
        <div className={`playmat-container${this.state.capturing ? " capturing" : ""}`} ref={this.containerRef} onClick={(e) => e.stopPropagation()}>
          <div className="playmat-header-actions hide-on-capture">
            <button className="screenshot-btn playmat-screenshot-btn" onClick={this.handleScreenshot} aria-label="Save screenshot" disabled={this.state.capturing}>
              <CameraAltIcon style={{ fontSize: 20 }} />
            </button>
            <button className="playmat-close-btn" onClick={onClose} aria-label="Close table view">
              <CloseIcon />
            </button>
          </div>

          <h2 className="playmat-deck-name">{deck.name}</h2>

          {field.length > 0 ? (
            <div className="playmat-layout">
              <div className="playmat-sidebar">
                <div className="playmat-field-section">
                  <h3 className="playmat-section-title">Field</h3>
                  <div className="playmat-field-cards">
                    {field.map(({ card }) => (
                      <div
                        key={card._id}
                        className="playmat-field-card"
                        onClick={() => onOpenModal(card, true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onOpenModal(card, true);
                          }
                        }}
                        aria-label={`View ${card.name}`}
                      >
                        <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="playmat-main">
                {this.renderMainSections(characters, strategies, abilities, onOpenModal, deck)}
              </div>
            </div>
          ) : (
            <div className="playmat-table">
              {this.renderMainSections(characters, strategies, abilities, onOpenModal, deck)}
            </div>
          )}
        </div>
      </div>
    );
  }
}
